import { FocusEvent, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isFailedAnswer } from '@connect-x-app/hybrid-state';
import { COLORS } from '@connect-x-app/shared-ui';
import { CONNECTED_TURNAROUND_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Icon } from '@connect-x-app/web-ui';
import type { UIMessage } from '@tanstack/ai-react';
import classnames from 'classnames';

import { usePanelSize } from '../../hooks/usePanelSize';
import { ChatComposer } from '../chatComposer/ChatComposer';
import { ChatMessage } from '../chatMessage/ChatMessage';

import './ConnectedTurnaroundPanel.scss';

type Props = {
  draft: string;
  isRunning: boolean;
  messages: readonly UIMessage[];
  onClose: () => void;
  onDraftChange: (draft: string) => void;
  retry: () => void;
  send: (question: string) => void;
  stop: () => void;
};

const isTurn = (message: UIMessage) => message.role === 'user' || message.role === 'assistant';

const hasAnswerText = (message?: UIMessage) =>
  message?.role === 'assistant' && message.parts.some(part => part.type === 'text' && part.content.trim().length > 0);

const showsError = (turns: readonly UIMessage[], index: number) =>
  isFailedAnswer(turns[index]) && turns[index + 1]?.role !== 'assistant';

export const ConnectedTurnaroundPanel = ({ draft, isRunning, messages, onClose, onDraftChange, retry, send, stop }: Props) => {
  const { t } = useTranslation();
  const { isExpanded, panelRef, size, startResize, toggleExpanded } = usePanelSize();
  const turns = messages.filter(isTurn);
  const isThinking = isRunning && !hasAnswerText(turns.at(-1));
  const lastIndex = turns.length - 1;
  const questionRef = useRef<HTMLInputElement>(null);
  const focusedRef = useRef<EventTarget>(null);

  const rememberFocus = useCallback((event: FocusEvent) => {
    focusedRef.current = event.target;
  }, []);

  const forgetFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      focusedRef.current = null;
    }
  }, []);

  useEffect(() => {
    const focused = focusedRef.current;
    if (focused instanceof Node && !focused.isConnected) {
      questionRef.current?.focus();
    }
  }, [isRunning]);

  return (
    <section
      aria-label={t('CONNECTED_TURNAROUND.TITLE')}
      className={classnames('connected-turnaround-panel', { 'connected-turnaround-panel--expanded': isExpanded })}
      data-test={CONNECTED_TURNAROUND_TEST_IDS.panel.view}
      onBlur={forgetFocus}
      onFocus={rememberFocus}
      ref={panelRef}
      style={isExpanded ? undefined : size}
    >
      {isExpanded ? null : (
        <span
          aria-hidden
          className="connected-turnaround-panel__resize"
          data-test={CONNECTED_TURNAROUND_TEST_IDS.panel.resize}
          onPointerDown={startResize}
        />
      )}
      <header className="connected-turnaround-panel__header">
        <h2 className="connected-turnaround-panel__title">{t('CONNECTED_TURNAROUND.TITLE')}</h2>
        <div className="connected-turnaround-panel__actions">
          <Icon
            colorName={COLORS.zulu.$08}
            label={t(isExpanded ? 'CONNECTED_TURNAROUND.COLLAPSE' : 'CONNECTED_TURNAROUND.EXPAND')}
            name={isExpanded ? 'directional2arrowsInwardTemplateOutline' : 'directional2arrowsOutwardTemplateOutline'}
            onClick={toggleExpanded}
            testId={CONNECTED_TURNAROUND_TEST_IDS.panel.expand}
          />
          <Icon
            colorName={COLORS.zulu.$08}
            label={t('CONNECTED_TURNAROUND.CLOSE')}
            name="close"
            onClick={onClose}
            testId={CONNECTED_TURNAROUND_TEST_IDS.panel.close}
          />
        </div>
      </header>
      <div className="connected-turnaround-panel__messages">
        <div className="connected-turnaround-panel__conversation">
          {turns.map((message, index) => (
            <ChatMessage
              index={index}
              isLatest={index === lastIndex}
              key={message.id}
              message={message}
              onRetry={index === lastIndex && !isRunning ? retry : undefined}
              showsError={showsError(turns, index)}
            />
          ))}
          <div
            aria-live="polite"
            className="connected-turnaround-panel__status"
            data-test={CONNECTED_TURNAROUND_TEST_IDS.messages.status}
          >
            {isThinking ? (
              <div className="connected-turnaround-panel__thinking" data-test={CONNECTED_TURNAROUND_TEST_IDS.messages.thinking}>
                <span aria-hidden className="connected-turnaround-panel__dots">
                  <span />
                  <span />
                  <span />
                </span>
                {t('CONNECTED_TURNAROUND.THINKING')}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <ChatComposer
        draft={draft}
        isRunning={isRunning}
        onDraftChange={onDraftChange}
        onSend={send}
        onStop={stop}
        ref={questionRef}
      />
      <p className="connected-turnaround-panel__disclaimer" data-test={CONNECTED_TURNAROUND_TEST_IDS.panel.disclaimer}>
        {t('CONNECTED_TURNAROUND.DISCLAIMER')}
      </p>
    </section>
  );
};
